import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FiActivity,
  FiCheckCircle,
  FiDollarSign,
  FiPackage,
  FiRefreshCw,
  FiTrendingDown,
  FiTrendingUp
} from 'react-icons/fi';
import { API_ENDPOINTS } from '../../utils/api';
import { fetchAllInvoices } from '../../utils/zovcurInvoiceApi';
import { formatINR } from '../../utils/currency';
import {
  buildKpiSnapshot,
  formatKpiValue,
  getKpiProgress,
  getPeriodLabel
} from '../../utils/kpiUtils';

const KPICard = ({ title, icon: Icon, tone, kpis }) => {
  const toneClasses = {
    blue: 'bg-[#e8f0f4] text-[#1a365d]',
    teal: 'bg-teal-50 text-teal-700',
    purple: 'bg-indigo-50 text-indigo-700'
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-3 mb-5">
        <div className={`p-2.5 rounded-xl ${toneClasses[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
      </div>
      <div className="space-y-4">
        {kpis.map((kpi) => {
          const progress = getKpiProgress(kpi.value, kpi.target, kpi.lowerIsBetter);
          const achieved =
            kpi.lowerIsBetter
              ? Number(kpi.value) <= Number(kpi.target)
              : kpi.target > 0
                ? Number(kpi.value) >= Number(kpi.target)
                : Number(kpi.value) > 0;

          return (
            <div key={kpi.label}>
              <div className="flex justify-between items-center gap-3 mb-2">
                <span className="text-sm font-medium text-slate-700">{kpi.label}</span>
                <span
                  className={`text-xs sm:text-sm font-bold whitespace-nowrap ${
                    achieved ? 'text-emerald-600' : 'text-amber-600'
                  }`}
                >
                  {formatKpiValue(kpi.value, kpi.format)}
                  {kpi.unit ? (
                    <span className="text-slate-400 font-normal text-[10px] ml-1">{kpi.unit}</span>
                  ) : null}
                  {!kpi.optionalTarget ? (
                    <span className="text-slate-400 font-normal">
                      {' '}
                      / {formatKpiValue(kpi.target, kpi.format)}
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${achieved ? 'bg-emerald-500' : 'bg-amber-500'}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SummaryTile = ({ label, value, sub, trend, tone }) => {
  const tones = {
    blue: 'bg-[#e8f0f4]',
    green: 'bg-emerald-50',
    purple: 'bg-indigo-50'
  };
  const TrendIcon = trend >= 0 ? FiTrendingUp : FiTrendingDown;
  const trendColor = trend >= 0 ? 'text-emerald-600' : 'text-rose-600';

  return (
    <div className={`p-4 rounded-2xl border border-slate-200 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {trend !== undefined && trend !== null ? (
          <TrendIcon className={trendColor} />
        ) : null}
      </div>
      <p className="text-2xl font-bold text-slate-800 mt-2">{value}</p>
      {sub ? <p className="text-xs text-slate-500 mt-1">{sub}</p> : null}
    </div>
  );
};

const KPIs = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [targets, setTargets] = useState([]);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [expiryStats, setExpiryStats] = useState({ total: 0, expired: 0, expiring: 0, safe: 0 });

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();

    const results = await Promise.allSettled([
      fetchAllInvoices(),
      axios.get(API_ENDPOINTS.getSalesTargets, { headers }),
      axios.get(API_ENDPOINTS.getStockTransactions, { headers }),
      axios.get(API_ENDPOINTS.getExpiryTracking, { headers })
    ]);

    setInvoices(results[0].status === 'fulfilled' ? results[0].value || [] : []);
    setTargets(results[1].status === 'fulfilled' ? results[1].value.data || [] : []);
    setStockTransactions(results[2].status === 'fulfilled' ? results[2].value.data || [] : []);

    if (results[3].status === 'fulfilled') {
      setExpiryStats(results[3].value.data?.stats || { total: 0, expired: 0, expiring: 0, safe: 0 });
    } else {
      setExpiryStats({ total: 0, expired: 0, expiring: 0, safe: 0 });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const snapshot = useMemo(
    () =>
      buildKpiSnapshot({
        invoices,
        targets,
        stockTransactions,
        expiryStats,
        periodType: selectedPeriod
      }),
    [invoices, targets, stockTransactions, expiryStats, selectedPeriod]
  );

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
              <FiActivity className="text-teal-600" />
              Key Performance Indicators
            </h1>
            <p className="text-slate-600 mt-1">
              Live production, sales, and compliance metrics — {getPeriodLabel(selectedPeriod)}
            </p>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg bg-white"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2"
            >
              <FiRefreshCw /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
            <p className="text-slate-500 mt-3 text-sm">Loading KPI data…</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
              <SummaryTile
                label="Sales Revenue"
                value={formatINR(snapshot.currentSales.revenue)}
                sub={
                  snapshot.periodTargets.amountTarget
                    ? `${snapshot.summary.salesReach}% of target`
                    : `${snapshot.summary.revenueGrowth.toFixed(1)}% vs previous period`
                }
                trend={snapshot.summary.revenueGrowth}
                tone="green"
              />
              <SummaryTile
                label="Strips Sold"
                value={snapshot.currentSales.strips.toLocaleString('en-IN')}
                sub={
                  snapshot.periodTargets.stripTarget
                    ? `${snapshot.summary.stripReach}% of target`
                    : `${snapshot.summary.stripGrowth.toFixed(1)}% vs previous period`
                }
                trend={snapshot.summary.stripGrowth}
                tone="blue"
              />
              <SummaryTile
                label="Stock Out Quantity"
                value={snapshot.summary.stockOutStrips.toLocaleString('en-IN')}
                sub={
                  snapshot.summary.stockOutSamples
                    ? `${snapshot.summary.stockOutSamples} sample boxes · ${snapshot.summary.stockOutQty} total strips`
                    : `${snapshot.summary.stockOutQty} strips dispatched`
                }
                tone="blue"
              />
              <SummaryTile
                label="Stock In Quantity"
                value={snapshot.summary.stockInQty.toLocaleString('en-IN')}
                sub="boxes received"
                tone="blue"
              />
              <SummaryTile
                label="Production Efficiency"
                value={`${snapshot.summary.productionEfficiency.toFixed(1)}%`}
                sub="Invoice strips vs stock in capacity"
                tone="blue"
              />
              <SummaryTile
                label="Compliance Score"
                value={`${snapshot.summary.complianceScore}%`}
                sub={`${snapshot.currentSales.paidCount} paid · ${snapshot.currentSales.unpaidCount} unpaid`}
                tone="purple"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <KPICard
                title="Production KPIs"
                icon={FiPackage}
                tone="blue"
                kpis={snapshot.productionKPIs}
              />
              <KPICard
                title="Sales KPIs"
                icon={FiDollarSign}
                tone="teal"
                kpis={snapshot.salesKPIs}
              />
              <KPICard
                title="Compliance KPIs"
                icon={FiCheckCircle}
                tone="purple"
                kpis={snapshot.complianceKPIs}
              />
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Period Snapshot</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 text-sm">
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Invoices</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {snapshot.currentSales.invoiceCount}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Stock In</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {snapshot.stock.stockInQty.toLocaleString('en-IN')} boxes
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Stock Out (Invoices)</p>
                  <p className="text-xl font-bold text-teal-700 mt-1">
                    {snapshot.stock.stockOutStrips.toLocaleString('en-IN')} strips
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Stock Out (Samples)</p>
                  <p className="text-xl font-bold text-amber-700 mt-1">
                    {snapshot.stock.stockOutSamples.toLocaleString('en-IN')} boxes
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Revenue Target</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {snapshot.periodTargets.amountTarget
                      ? formatINR(snapshot.periodTargets.amountTarget)
                      : 'Not set'}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 border border-slate-100">
                  <p className="text-slate-500">Collection Rate</p>
                  <p className="text-xl font-bold text-slate-800 mt-1">
                    {snapshot.currentSales.collectionRate.toFixed(1)}%
                  </p>
                </div>
              </div>
              {!snapshot.periodTargets.amountTarget && !snapshot.periodTargets.stripTarget ? (
                <p className="text-xs text-slate-500 mt-4">
                  Set sales targets in Sales Target Reports to compare revenue and strips against goals.
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KPIs;
